/**
 * @file        src/pages/platform/SupplierLogs.jsx
 * @description Platform · Supplier API Logs (super_admin only). Every
 *              TripJack call is captured server-side into the TENANT's own
 *              `supplier_api_logs` table (booking_backend migration
 *              2026-08-05_supplier_api_logs.sql, written by
 *              middleware/axiosInstance.js#attachLogging). This screen lets
 *              the super admin pick a tenant + brand + date range and browse
 *              request/response payloads for dispute/investigation lookups.
 *              No retention/purge — rows are kept indefinitely.
 * @author      Kinetika Labs
 * @created     2026-08-05
 * @changelog
 *   - 2026-08-05 Claude — Initial.
 *   - 2026-08-05 Claude — Defaults to today + the tenant/brand picked first,
 *                          so the screen shows something useful on open
 *                          instead of an empty "pick a tenant" state. `to` is
 *                          now sent end-of-day inclusive (was truncating to
 *                          00:00, silently hiding same-day rows). Added
 *                          Product/Operation tag chips from the backend's
 *                          classify(). Row-expand detail is now a bordered,
 *                          wrapped code box (was raw unwrapped text bleeding
 *                          across the page).
 *   - 2026-08-06 Claude — Added Endpoint/Product/Operation filters (backend:
 *                          tenants.routes.js supplierLogsQuery). Endpoint
 *                          column now shows a `title` tooltip with the full
 *                          URL — the clipped `text-overflow: ellipsis` cell
 *                          had no way to reveal the truncated part before
 *                          this, which read as "endpoints not showing
 *                          correctly" (nothing indicated it was cut off).
 *   - 2026-09-25 Claude — `keepPreviousData: true` is a React Query **v4** option
 *                          and this workspace is on v5 (5.101), where no such
 *                          option exists and it was silently ignored. So every
 *                          page / filter / search change dropped `data` to
 *                          undefined, the table was swapped for six skeleton rows
 *                          and the panel collapsed ~700px before growing back —
 *                          the "pages flicker / the page moves" report. v5 spells
 *                          it `placeholderData: keepPreviousData` (a FUNCTION
 *                          imported from the package, not a boolean).
 */
import { Fragment, useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Select from '../../components/Select';
import Chip from '../../components/Chip';
import Pager from '../../components/Pager';
import TableSkeleton from '../../components/TableSkeleton';
import { get } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { formatDateTime } from '../../lib/format';

function safeParse(v) {
  try { return v ? JSON.parse(v) : null; } catch { return v; }
}

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

const PRODUCT_TONE = { flight: 'warn', hotel: 'ok', insurance: 'pend' };
const OPERATION_TONE = { search: 'pend', booking: 'ok', refund: 'rej', payment: 'warn', other: 'pend' };

// Mirror admin_backend/src/utils/supplierLogClassify.js PRODUCTS/OPERATIONS —
// the enums the backend actually validates against.
const PRODUCT_OPTIONS = [
  { value: '', label: 'All products' },
  { value: 'flight', label: 'Flight' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'insurance', label: 'Insurance' },
];
const OPERATION_OPTIONS = [
  { value: '', label: 'All operations' },
  { value: 'search', label: 'Search' },
  { value: 'booking', label: 'Booking' },
  { value: 'refund', label: 'Refund' },
  { value: 'payment', label: 'Payment' },
  { value: 'other', label: 'Other' },
];

const codeBoxStyle = {
  background: 'var(--field)',
  border: '1px solid var(--stroke)',
  borderRadius: 12,
  padding: 12,
  overflowX: 'auto',
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxHeight: 360,
  overflowY: 'auto',
};

export default function SupplierLogs() {
  const [tenantId, setTenantId] = useState('');
  const [brand, setBrand] = useState('');
  // Tracks whether the CURRENT tenant's brand was picked by the user (or
  // deliberately set to "All brands"), vs. still waiting on the default. Reset
  // whenever the tenant changes so each tenant re-defaults to its own first
  // brand, but — critically — left alone on background refetches, so a
  // react-query refresh of the brand list never silently stomps the admin's
  // manual choice back to brands[0].
  const [brandTouched, setBrandTouched] = useState(false);
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  // Endpoint is free text over potentially thousands of rows — applied on
  // Enter/blur (matching the search/applied pattern other list pages use,
  // e.g. Transactions.jsx) rather than firing a query per keystroke.
  const [endpointInput, setEndpointInput] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [product, setProduct] = useState('');
  const [operation, setOperation] = useState('');
  const [page, setPage] = useState(1);
  const [openId, setOpenId] = useState(null);
  const pageSize = 25;

  const applyEndpoint = () => { setEndpoint(endpointInput.trim()); setPage(1); };

  const { data: tenants } = useQuery({
    queryKey: qk.tenants.list({ pageSize: 200 }),
    queryFn: () => get('/tenants', { params: { pageSize: 200 } }),
  });

  // Default to the first tenant once the list loads, so the screen shows
  // real data on open instead of an empty "select a tenant" state.
  useEffect(() => {
    if (!tenantId && tenants?.data?.length) setTenantId(String(tenants.data[0].id));
  }, [tenants, tenantId]);

  const { data: brands } = useQuery({
    queryKey: qk.tenants.brands(tenantId),
    queryFn: () => get(`/tenants/${tenantId}/brands`).then((r) => r.data),
    enabled: Boolean(tenantId),
  });

  // Default to that tenant's first brand (not "All brands") the first time its
  // brand list loads. Guarded by brandTouched so this never re-fires and
  // overrides a manual pick on a later background refetch.
  useEffect(() => {
    if (!brandTouched && brands?.length) setBrand(brands[0].code);
  }, [brands, brandTouched]);

  const listParams = {
    page,
    pageSize,
    ...(brand ? { brand } : {}),
    ...(endpoint ? { endpoint } : {}),
    ...(product ? { product } : {}),
    ...(operation ? { operation } : {}),
    ...(from ? { from } : {}),
    // Inclusive end-of-day — a bare date string compares as 00:00:00 and
    // would silently exclude every row from "to" itself.
    ...(to ? { to: `${to} 23:59:59.999` } : {}),
  };
  const { data: list, isLoading } = useQuery({
    queryKey: qk.tenants.supplierLogs(tenantId, listParams),
    queryFn: () => get(`/tenants/${tenantId}/supplier-logs`, { params: listParams }),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });

  const { data: detail } = useQuery({
    queryKey: qk.tenants.supplierLog(tenantId, openId),
    queryFn: () => get(`/tenants/${tenantId}/supplier-logs/${openId}`).then((r) => r.data),
    enabled: Boolean(tenantId && openId),
  });

  const rows = list?.data || [];
  const total = list?.meta?.total || 0;
  const tenantOptions = (tenants?.data || []).map((t) => ({ value: String(t.id), label: t.name }));
  const brandOptions = [{ value: '', label: 'All brands' }, ...(brands || []).map((b) => ({ value: b.code, label: b.name }))];

  return (
    <>
      <PageHead title="Supplier API Logs" subtitle="Platform · TripJack request/response capture, per tenant" />

      <article className="panel glass">
        <div className="search-row">
          <div className="field">
            <label>Tenant</label>
            <Select
              value={tenantId}
              onChange={(v) => { setTenantId(v); setBrand(''); setBrandTouched(false); setPage(1); setOpenId(null); }}
              options={tenantOptions}
              placeholder="Select tenant"
            />
          </div>
          <div className="field">
            <label>Brand</label>
            <Select
              value={brand}
              onChange={(v) => { setBrand(v); setBrandTouched(true); setPage(1); }}
              options={brandOptions}
              placeholder="All brands"
            />
          </div>
          <div className="field">
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>Product</label>
            <Select
              value={product}
              onChange={(v) => { setProduct(v); setPage(1); }}
              options={PRODUCT_OPTIONS}
              placeholder="All products"
            />
          </div>
          <div className="field">
            <label>Operation</label>
            <Select
              value={operation}
              onChange={(v) => { setOperation(v); setPage(1); }}
              options={OPERATION_OPTIONS}
              placeholder="All operations"
            />
          </div>
          <div className="field">
            <label>Endpoint</label>
            <input
              type="text"
              placeholder="e.g. air-search, /book"
              value={endpointInput}
              onChange={(e) => setEndpointInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') applyEndpoint(); }}
              onBlur={applyEndpoint}
            />
          </div>
        </div>

        {!tenantId ? (
          <div className="empty">Select a tenant to browse its supplier logs.</div>
        ) : (
          <>
            <div className="table-wrap">
              {isLoading ? <TableSkeleton /> : (
                <table className="table">
                  <thead>
                    <tr>
                      <th>Time</th><th>Brand</th><th>Product</th><th>Operation</th><th>Method</th><th>Endpoint</th><th>Status</th><th>Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr><td colSpan={8} className="empty">No supplier calls for these filters.</td></tr>
                    ) : rows.map((r) => (
                      <Fragment key={r.id}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setOpenId(openId === r.id ? null : r.id)}>
                          <td className="muted">{formatDateTime(r.created_at)}</td>
                          <td>{r.brand || <span className="muted">—</span>}</td>
                          <td><Chip tone={PRODUCT_TONE[r.product] || 'pend'}>{r.product}</Chip></td>
                          <td><Chip tone={OPERATION_TONE[r.operation] || 'pend'}>{r.operation}</Chip></td>
                          <td className="mono">{r.method}</td>
                          <td
                            className="mono"
                            title={r.endpoint}
                            style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {r.endpoint}
                          </td>
                          <td><Chip tone={r.is_error ? 'rej' : 'ok'}>{r.status_code ?? (r.is_error ? 'ERR' : '—')}</Chip></td>
                          <td className="muted">{r.duration_ms != null ? `${r.duration_ms} ms` : '—'}</td>
                        </tr>
                        {openId === r.id ? (
                          <tr>
                            <td colSpan={8}>
                              {!detail ? <TableSkeleton rows={2} /> : (
                                <div style={{ padding: '10px 4px' }}>
                                  <p className="mono muted" style={{ wordBreak: 'break-all', marginBottom: 10 }}>{r.method} {r.endpoint}</p>
                                  {r.error_message ? <p className="field-error">{r.error_message}</p> : null}
                                  <b>Request</b>
                                  <pre className="mono" style={{ ...codeBoxStyle, marginBottom: 12, marginTop: 6 }}>
                                    {JSON.stringify(safeParse(detail.request_body), null, 2)}
                                  </pre>
                                  <b>Response</b>
                                  <pre className="mono" style={{ ...codeBoxStyle, marginTop: 6 }}>
                                    {JSON.stringify(safeParse(detail.response_body), null, 2)}
                                  </pre>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <Pager page={page} pageSize={pageSize} total={total} onPage={(p) => { setPage(p); setOpenId(null); }} />
          </>
        )}
      </article>
    </>
  );
}
